import { select, put, call, takeLatest, all } from 'redux-saga/effects';
import API from 'services/api';

import { convertPercentageToimageCordinates } from 'utils/DataTransform/CleanerTask/CleanerTaskDetails';

import { showSnackbar, setLoading, modalState, toggleModal } from 'reducers/ui';
import { errorHandler } from 'utils/errorMessages';
import { getTimezone } from '../../reducers/utils';

import { getUserId, getUserSelectedLanguage } from 'reducers/users';

import { getGlobalListSaga, getGlobalUsers } from '../globalList';
import { createSiteRequest } from 'sagas/sitesSaga/sitesSaga';
import { TASK_MODEL } from '../../services/entity/task';
import { isBase64 } from 'utils/checkImage';
import { v4 } from 'uuid';
import { MEDIA_LOCATION } from 'services/media';
import { assignNewSiteToSubtask } from 'utils/helper/task';
import isEmpty from 'lodash/isEmpty';
import { DB_NULL, PAGE_NAME } from 'utils/constants';
import { actions as dataGridActions } from '../../containers/Pages/Tasks/Template/Modules/dataGridTable';
import { actions as templateFilterActions } from '../../containers/Pages/Tasks/Template/Modules/filters';
import * as templateSelectors from '../../containers/Pages/Tasks/Template/Selectors/templateSelectors';
import { linkFollowupTaskIdToEvents } from 'sagas/eventsSaga/eventsSaga';

export function* fetchTaskTemplatesListSaga({ payload }) {
  try {
    const { filters, from } = payload;
    const filterKeyStatus = yield select(
      templateSelectors.getTemplateFilterKey,
    );
    const forceRefresh = yield select(templateSelectors.getForceRefresh);
    const { data } = yield call(API.fetchTaskTemplates, {
      ...filters,
      from,
    });
    if (data.data) {
      const { meta } = data;
      yield put(
        dataGridActions.setGridList({
          list: data.data,
          totalEntries: meta.total,
        }),
      );
    }
    if (filterKeyStatus) {
      yield put(templateFilterActions.setFilterKey(false));
    }
    if (forceRefresh) {
      yield put(dataGridActions.setForceRefresh(false));
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* fetchTemplateTaskDetailsSaga({ payload }) {
  const { id, dummyTaskData } = payload;
  try {
    yield put(setLoading(true));
    const { data } = yield call(API.fetchTaskDetails, { id });

    if (data.data) {
      // eslint-disable-next-line prefer-const
      let { tasks, joins, surfaces } = data.data;

      // if the surface id is set to null then dont convert surfaceU and surfacev
      joins = convertPercentageToimageCordinates(joins, surfaces);
      tasks = convertPercentageToimageCordinates(tasks, surfaces);
      if (Array.isArray(tasks) && tasks.length > 0) {
        data.data['tasks'] = tasks;
        data.data['joins'] = joins;
        //call the global Options for users, sites, business clients
        yield call(getGlobalUsers);
        yield call(getGlobalListSaga, {
          category: 'sites',
        });
        yield call(getGlobalListSaga, {
          category: 'businessClients',
        });

        yield put(
          dataGridActions.setGridRowDetails({
            details: data.data,
            dummyRowData: dummyTaskData,
          }),
        );
      }
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
  yield put(setLoading(false));
}

export function* preTaskCondition({ media, siteData, ...task }) {
  try {
    if (siteData) {
      const newSiteResponse = yield call(createSiteRequest, {
        site: { ...siteData },
      });

      if (!newSiteResponse) {
        yield put(setLoading(false));
        throw newSiteResponse;
      }

      task[TASK_MODEL.siteId.id] = newSiteResponse.data.id;
      task[TASK_MODEL.businessClientId.id] =
        newSiteResponse.data.businessClient.id;
    }

    if (Array.isArray(task.schedulePatterns)) {
      const schedulePatternPayload = yield all(
        task.schedulePatterns.map(task => call(preTaskCondition, task)),
      );

      task.schedulePatterns = schedulePatternPayload;
    }

    if (media) {
      let imageUrl = media.image;

      if (isBase64(media.image)) {
        const mediaId = v4();
        const userId = yield select(getUserId);
        const { service, image } = media;
        yield call(API.uploadMediaItem, {
          userId,
          mediaId,
          data: {
            service,
            base64: image,
          },
        });

        imageUrl = `${MEDIA_LOCATION}/${userId}/${mediaId}/image`;
      }

      task.descriptionImageUrl = imageUrl;
    }

    return task;
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
    throw err;
  }
}

export function* createSimpleTask({ data: taskData, rootTaskId }) {
  try {
    yield put(setLoading(true));
    const { tasks } = taskData;

    const tasksPayload = yield all(
      tasks.map(task => call(preTaskCondition, task)),
    );

    const { data } = yield call(API.taskUpload, {
      data: {
        ...taskData,
        tasks: assignNewSiteToSubtask({
          rootTaskId,
          tasks,
          tasksPayload,
        }),
      },
    });

    return data;
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
    return false;
  }
}

export function* createTemplateTaskSaga({ payload }) {
  const { data: taskData, rootTaskId, fromEvents } = payload;
  const assigneeName = taskData.tasks[0].assigneeFullName;
  try {
    yield put(setLoading(true));
    const filterKeyStatus = yield select(
      templateSelectors.getTemplateFilterKey,
    );
    const forceRefresh = yield select(templateSelectors.getForceRefresh);
    const { data } = yield call(createSimpleTask, {
      data: taskData,
      rootTaskId,
    });

    if (data) {
      if (fromEvents) {
        // send the root task Id to the event saga for linking the followup task Id to the respective event
        yield call(linkFollowupTaskIdToEvents, { payload: { rootTaskId } });
        return;
      }
      const detailPanelActive = yield select(
        templateSelectors.getGridRowDetailsTransformed,
      );

      if (!isEmpty(detailPanelActive)) {
        yield call(fetchTemplateTaskDetailsSaga, {
          payload: {
            id: detailPanelActive.others.id.value,
            dummyTaskData: { id: detailPanelActive.others.id.value },
          },
        });
      }

      const isModelOpen = yield select(modalState);
      if (isModelOpen[PAGE_NAME.TEMPLATE_TASKS]) {
        yield put(toggleModal(PAGE_NAME.TEMPLATE_TASKS));
      }
    }

    yield put(setLoading(false));

    if (filterKeyStatus) {
      yield put(templateFilterActions.setFilterKey(false));
    }
    if (forceRefresh) {
      yield put(dataGridActions.setForceRefresh(false));
    }

    if (assigneeName && assigneeName !== DB_NULL) {
      yield put(
        showSnackbar({
          typeOfSnackbar: 'success',
          message: 'Message sent',
        }),
      );
    }
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* getTaskQr({ payload }) {
  try {
    const { id } = payload;
    const { data } = yield call(API.getTaskQr, id);
    if (data.data) {
      const { url } = data.data;
      window.open(url, '_blank');
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* fetchTemplateTaskDetailsPdfSaga({ payload }) {
  try {
    const { id } = payload;
    const timezone = yield select(getTimezone);
    const language = yield select(getUserSelectedLanguage);
    const { data } = yield call(API.fetchCleanerTaskDetailsPdf, {
      id,
      timezone,
      language: language.toUpperCase(),
    });
    if (data.data) {
      const { url } = data.data;
      window.open(url, '_blank');
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* fetchTemplateTaskListPdfSaga({ payload }) {
  try {
    const { filters } = payload;
    const timezone = yield select(getTimezone);
    const language = yield select(getUserSelectedLanguage);
    const { data } = yield call(API.fetchCleanerTaskListPdf, {
      ...filters,
      language: language.toUpperCase(),
      timezone,
    });
    if (data.data) {
      const { url } = data.data;
      window.open(url, '_blank');
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* taskTemplateSagas() {
  yield takeLatest(dataGridActions.fetchGridList, fetchTaskTemplatesListSaga);
  yield takeLatest(
    dataGridActions.fetchGridRowDetails,
    fetchTemplateTaskDetailsSaga,
  );
  yield takeLatest(dataGridActions.createRowEntry, createTemplateTaskSaga);
  yield takeLatest(dataGridActions.updateRowEntry, createTemplateTaskSaga);
  yield takeLatest(dataGridActions.fetchQr, getTaskQr);
  yield takeLatest(
    dataGridActions.fetchGridRowDetailsPdf,
    fetchTemplateTaskDetailsPdfSaga,
  );
  yield takeLatest(
    dataGridActions.fetchGridListPdf,
    fetchTemplateTaskListPdfSaga,
  );
}
